import axios, { type AxiosInstance } from "axios"
import { getExhibitionHeader } from "../utils/exhibitionContext";
import type { StaffDashboardData, CountdownData } from "../Component/Dashboard/StaffDashboard/staff-dashboard"

const api: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://127.0.0.1:8000",
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 10000,
})

// Attach token before each request
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("accessToken")
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`
    Object.assign(config.headers, getExhibitionHeader());
    }
    return config
  },
  (error) => Promise.reject(error),
)

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("accessToken")
      window.location.href = "/login"
    }
    return Promise.reject(error)
  },
)

const StaffDashboardService = {
  // Get staff dashboard data
  async getStaffDashboardData(): Promise<StaffDashboardData> {
    try {
      const response = await api.get("/api/exhibitions/staff/dashboard-data/")
      if (response.data.success) {
        return response.data.data
      } else {
        throw new Error(response.data.error || "Failed to fetch staff dashboard data")
      }
    } catch (error) {
      console.error("Error fetching staff dashboard data:", error)
      throw new Error("Failed to fetch staff dashboard data")
    }
  },

  // Get exhibition countdown data
  async getExhibitionCountdown(exhibitionId?: number): Promise<CountdownData> {
    try {
      const url = exhibitionId
        ? `/api/exhibitions/staff/exhibition-countdown/${exhibitionId}/`
        : "/api/exhibitions/staff/exhibition-countdown/"

      const response = await api.get(url)
      if (response.data.success) {
        return response.data.data
      } else {
        throw new Error(response.data.error || "Failed to fetch countdown data")
      }
    } catch (error) {
      console.error("Error fetching exhibition countdown:", error)
      throw new Error("Failed to fetch exhibition countdown data")
    }
  },

  // Get exhibition list for staff
  async getExhibitionList(): Promise<any[]> {
    try {
      const response = await api.get("/api/exhibitions/staff/list/")
      if (response.data.success) {
        return response.data.data
      } else {
        throw new Error(response.data.error || "Failed to fetch exhibition list")
      }
    } catch (error) {
      console.error("Error fetching exhibition list:", error)
      throw new Error("Failed to fetch exhibition list")
    }
  },

  // Get exhibitor statistics for staff
  async getExhibitorStats(): Promise<any> {
    try {
      const response = await api.get("/api/exhibitions/staff/exhibitor-stats/")
      if (response.data.success) {
        return response.data.data
      } else {
        throw new Error(response.data.error || "Failed to fetch exhibitor stats")
      }
    } catch (error) {
      console.error("Error fetching exhibitor stats:", error)
      throw new Error("Failed to fetch exhibitor statistics")
    }
  },
}

export default StaffDashboardService
